<template>
  <div>
    <h1 class="mb-4">{{ title }}</h1>
    <div class="table-responsive">
      <b-table
        responsive
        id="users"
        outlined
        :items="list"
        :fields="fields"
        :per-page="perPage"
        :current-page="currentPage"
        show-empty
      >
        <template slot="name" slot-scope="{ item }">
          <router-link
            :to="{
              name: 'hunter-profile-admin',
              params: { userId: item.userId }
            }"
          >
            {{ item.name }}
          </router-link>
        </template>
        <template slot="occuredAt" slot-scope="{ item }">
          <span class="text-light mr-2">{{
            $date.format(item.occurredAt, 'DD.MM.YYYY')
          }}</span>
          <sup class="text-light">{{
            $date.format(item.occurredAt, 'HH.mm')
          }}</sup>
        </template>
        <template slot="wallet" slot-scope="{ item }">
          <span class="text-light">{{ item.walletType }}:</span>
          {{ item.wallet }}
        </template>
        <template slot="status" slot-scope="{ item }">
          <b-badge
            :variant="statusClass(item.status)"
            class="text-uppercase"
            data-cy="withdrawal-request-status"
          >
            {{ $t(`statuses.${item.status}`) }}
          </b-badge>
        </template>
        <template slot="actions" slot-scope="{ item }">
          <button
            class="btn btn-link btn-sm"
            @click="edit(item)"
            data-cy="edit-withdrawal-request"
          >
            <svg-icon icon="pencil" class="mr-1" />
            <span>{{ $t('edit') }}</span>
          </button>
        </template>
      </b-table>
    </div>
    <b-row class="withdrawals-bottom flex-wrap">
      <b-col sm="6" class="d-flex d-sm-block justify-content-center">
        <b-pagination
          v-model="currentPage"
          :total-rows="totalRows"
          :per-page="perPage"
          aria-controls="users"
        />
      </b-col>
      <b-col sm="6" class="d-flex d-sm-block justify-content-center">
        <div class="d-flex flex-row-reverse">
          <b-form-group
            label-cols="6"
            :label="$t('perPage')"
            class="mb-0 ml-auto"
          >
            <b-form-select
              v-model="perPage"
              :options="pageOptions"
              style="width: 65px"
            />
          </b-form-group>
        </div>
      </b-col>
    </b-row>

    <b-modal
      :title="$t('withdrawalRequest')"
      hide-footer
      id="withdraw-edit-modal"
      ref="withdraw-edit-modal"
      class="withdrawal-modal"
    >
      <div v-if="modal.info" data-cy="edit-withdrawal-request-modal">
        <div>
          <pre>
            {{ $t('name') }}: {{ modal.info.name }} <br>
            {{ $t('date') }}: {{ this.$date.format(modal.info.occurredAt, 'DD.MM.YYYY') }} <br>
            {{ $t('amount') }}: {{ modal.info.amount }} <br>
            {{ $tc('wallet', 1) }}: {{ modal.info.walletType }} - {{ modal.info.wallet }} <br>
            {{ $tc('comment', 1) }}: {{ modal.info.comment }} <br>
          </pre>
        </div>
        <b-form-group>
          <b-form-label
            slot="label"
            :label="$t('leaveComment')"
            for="withdraw-comment"
          />
          <b-form-textarea id="withdraw-comment" v-model="modal.form.comment" />
        </b-form-group>
        <div class="text-right">
          <button
            @click="submit('approved')"
            class="btn btn-primary btn-success btn-sm"
            data-cy="approve-request"
          >
            {{ $t('approve') }}
          </button>
          <button
            @click="submit('rejected')"
            class="btn btn-primary btn-danger btn-sm"
            data-cy="reject-request"
          >
            {{ $t('reject') }}
          </button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { statusClassesMixin } from '@/utils/mixins.js';

export default {
  mixins: [statusClassesMixin],

  data() {
    return {
      modal: {
        form: {
          comment: null,
          status: null
        },
        info: null
      },
      currentPage: 1,
      list: null,
      fields: [
        {
          key: 'name',
          label: this.$t('name'),
          tdClass: 'pl-4'
        },
        {
          label: this.$t('date'),
          key: 'occuredAt',
          thClass: 'text-nowrap border-top-0',
          tdClass: 'pl-4'
        },
        {
          label: this.$t('amount'),
          key: 'amount',
          sortable: false,
          tdClass: 'pl-4'
        },
        {
          label: this.$tc('wallet', 1),
          key: 'wallet',
          thClass: 'text-nowrap',
          sortable: false,
          tdClass: 'pl-4'
        },
        {
          label: this.$tc('comment', 1),
          key: 'comment',
          sortable: false,
          tdClass: 'pl-4'
        },
        {
          label: this.$tc('status', 1),
          key: 'status',
          sortable: false,
          tdClass: 'pl-4'
        },
        {
          label: '',
          name: 'actions',
          key: 'actions',
          tdClass: 'align-middle'
        }
      ],
      perPage: 20,
      pageOptions: [5, 10, 20, 30, 50]
    };
  },

  computed: {
    title() {
      return `${this.$config.hostName}'s withrawal requests`;
    },
    totalRows() {
      return this.list ? this.list.length : 1;
    }
  },

  methods: {
    edit(data) {
      this.modal.info = data;
      this.$refs['withdraw-edit-modal'].show();
    },
    submit(status) {
      const form = {
        status: status,
        comment: this.modal.form.comment
      };
      const requestId = this.modal.info.id;

      this.$api.bounty.put(`/v0/withdrawal-requests/${requestId}`, form).then(
        () => {
          this.modal.form.comment = null;
          this.errors = {};
          this.$refs['withdraw-edit-modal'].hide();
          this.loadWithdrawalRequests();
          this.$notify('alert', {
            text: `Request was ${status}!`,
            variant: 'info',
            time: 5000
          });
        },
        e => {
          this.errors = {};

          if (e.response.data.errors) {
            this.errors = this.$parseErrors(e.response.data.errors) || {};
          } else {
            e.response.data.detail &&
              this.$notify('alert', {
                text: e.response.data.detail,
                variant: 'danger',
                time: 0
              });
          }
        }
      );
    },
    loadWithdrawalRequests() {
      this.$api.bounty
        .get('/v0/withdrawal-requests')
        .then(res => {
          this.list = res.data;
        })
        .catch(e => {
          this.$store.commit('SET_PAGE_FAILURE', e);
        });
    }
  },

  created() {
    this.loadWithdrawalRequests();
  }
};
</script>

<style lang="scss">
.withdrawals-bottom {
  legend {
    display: flex;
    align-items: center;
    justify-content: flex-end;
  }
}
pre {
  white-space: normal;
}
@include media-breakpoint-down(xs) {
  .withdrawals-bottom {
    legend {
      justify-content: start;
    }
  }
}
</style>
